// LatePenaltyRulesAdmin.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

/** ---------------- i18n ---------------- */
const MESSAGES = {
    en_US: {
        tabs: { manage: "Manage Rules", view: "View Penalties" },
        loading: { rules: "Loading rules…", penalties: "Loading penalties…" },
        buttons: {
            saveAll: "Save all",
            revert: "Revert",
            addRule: "+ Add rule",
            refresh: "Refresh",
            downloadCsv: "Download CSV",
            downloadCsvTooltip: "Download the currently filtered & sorted table as CSV",
            deleteRule: "Delete rule",
        },
        filters: {
            rulesPlaceholder: "Filter by label or status…",
            penaltiesPlaceholder: "Filter by term, course, student, assignment, or rule…",
            showing: "Showing {current} of {total}",
            courseFilter: "Course Filter:",
        },
        headings: {
            adminTitle: "Late Penalty Rules Admin",
            viewPenalties: "View Penalties",
            validFormats: "Valid Rule Formats",
        },
        examples: {
            ruleFormats:
                "“10% penalty if more than 2 hours late”, “10 points penalty if more than 1 day late”, “10% penalty each day late”, “1 point penalty each day late for a max of 10 points”.",
        },
        table: {
            actions: "Actions",
            label: "Label",
            status: "Status",
            noRules: "No rules yet.",
            sortByLabel: "Sort by label",
            sortByStatus: "Sort by status",
            sortByCourseId: "Sort by Course ID",
            sortByCourseName: "Sort by Course Name",
            sortByStudent: "Sort by Student",
            sortByAssignment: "Sort by Assignment",
            sortByRule: "Sort by Rule",
            sortByOverride: "Sort by Override",
            sortByPoints: "Sort by Points",
            sortByTerm: "Sort by Term",
        },
        status: { active: "Active", inactive: "Inactive" },
        field: {
            labelPlaceholder: "e.g., 5% penalty each day late",
            status: "Status",
        },
        toasts: { reverted: "Reverted unsaved changes", deleted: "Rule deleted", saved: "All changes saved" },
        errors: {
            genericFix: "Please fix the highlighted errors.",
            required: "Required",
            percentRange: "Percent must be between 0 and 100.",
            pointsGt0: "Points must be greater than 0.",
            thresholdGt0: "Threshold must be greater than 0.",
            ppdGt0: "Points per day must be greater than 0.",
            maxGt0: "Max points must be greater than 0.",
            maxGeRate: "Max points should be ≥ points per day.",
            unrecognized:
                "Unrecognized format. Examples: “10% penalty each day late”, “10 points penalty if more than 1 day late”, “1 point penalty each day late for a max of 10 points”.",
            somethingWrong: "Something went wrong. Please try again.",
        },
        penalties: {
            headers: {
                term: "Term",
                courseId: "Course ID",
                courseName: "Course Name",
                student: "Student",
                studentFirst: "Student First",
                studentLast: "Student Last",
                assignment: "Assignment",
                rule: "Rule",
                override: "Override",
                points: "Points",
            },
            none: "No penalty data.",
            rowsCount: "{count} rows",
        },
        bool: { yes: "Yes", no: "No" },
        gate: {
            checking: "Checking permissions…",
            blockedTitle: "Access Restricted",
            blockedMsg: "The Late Penalty Rules Admin Tool is not available for your role.",
        },
    },

    // English (Australia)
    en_AU: {
        tabs: { manage: "Manage Rules", view: "View Penalties" },
        loading: { rules: "Loading rules…", penalties: "Loading penalties…" },
        buttons: {
            saveAll: "Save all",
            revert: "Revert",
            addRule: "+ Add rule",
            refresh: "Refresh",
            downloadCsv: "Download CSV",
            downloadCsvTooltip: "Download the currently filtered & sorted table as CSV",
            deleteRule: "Delete rule",
        },
        filters: {
            rulesPlaceholder: "Filter by label or status…",
            penaltiesPlaceholder: "Filter by term, course, student, assignment, or rule…",
            showing: "Showing {current} of {total}",
            courseFilter: "Course Filter:",
        },
        headings: {
            adminTitle: "Late Penalty Rules Admin",
            viewPenalties: "View Penalties",
            validFormats: "Valid Rule Formats",
        },
        examples: {
            ruleFormats:
                "“10% penalty if more than 2 hours late”, “10 points penalty if more than 1 day late”, “10% penalty each day late”, “1 point penalty each day late for a max of 10 points”.",
        },
        table: {
            actions: "Actions",
            label: "Label",
            status: "Status",
            noRules: "No rules yet.",
            sortByLabel: "Sort by label",
            sortByStatus: "Sort by status",
            sortByCourseId: "Sort by Course ID",
            sortByCourseName: "Sort by Course Name",
            sortByStudent: "Sort by Student",
            sortByAssignment: "Sort by Assignment",
            sortByRule: "Sort by Rule",
            sortByOverride: "Sort by Override",
            sortByPoints: "Sort by Points",
            sortByTerm: "Sort by Term",
        },
        status: { active: "Active", inactive: "Inactive" },
        field: {
            labelPlaceholder: "e.g., 5% penalty each day late",
            status: "Status",
        },
        toasts: { reverted: "Reverted unsaved changes", deleted: "Rule deleted", saved: "All changes saved" },
        errors: {
            genericFix: "Please fix the highlighted errors.",
            required: "Required",
            percentRange: "Percent must be between 0 and 100.",
            pointsGt0: "Points must be greater than 0.",
            thresholdGt0: "Threshold must be greater than 0.",
            ppdGt0: "Points per day must be greater than 0.",
            maxGt0: "Max points must be greater than 0.",
            maxGeRate: "Max points should be ≥ points per day.",
            unrecognized:
                "Unrecognized format. Examples: “10% penalty each day late”, “10 points penalty if more than 1 day late”, “1 point penalty each day late for a max of 10 points”.",
            somethingWrong: "Something went wrong. Please try again.",
        },
        penalties: {
            headers: {
                term: "Term",
                courseId: "Course ID",
                courseName: "Course Name",
                student: "Student",
                studentFirst: "Student First",
                studentLast: "Student Last",
                assignment: "Assignment",
                rule: "Rule",
                override: "Override",
                points: "Points",
            },
            none: "No penalty data.",
            rowsCount: "{count} rows",
        },
        bool: { yes: "Yes", no: "No" },
        gate: {
            checking: "Checking permissions…",
            blockedTitle: "Access Restricted",
            blockedMsg: "The Late Penalty Rules Admin Tool is not available for your role.",
        },
    }
};

function getMessages(locale) {
    return MESSAGES[locale] || MESSAGES.en_US;
}
function tKey(msgs, path) {
    return path.split(".").reduce((acc, k) => (acc && acc[k] != null ? acc[k] : undefined), msgs);
}
function t(msgs, path, vars = {}) {
    const raw = tKey(msgs, path);
    if (raw == null) return path;
    if (typeof raw !== "string") return String(raw);
    return raw.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : ""));
}

/** ---------------- Backend Adapters ---------------- */
const RULES_PATH = "/late-penalty-rules";
const LPA_PATH = "/late-penalty-associations";

// ✨ Add tenantId to inject x-tenant-id header
function makeRulesBackend(backEndUrl, tenantId) {
    const base = (backEndUrl || "").replace(/\/+$/, "");
    const baseHeaders = { "Content-Type": "application/json", Accept: "application/json" };
    const http = axios.create({
        baseURL: base,
        timeout: 20000,
        headers: { ...baseHeaders, ...(tenantId ? { "x-tenant-id": tenantId } : {}) },
    });

    const listAll = async (limit = 200) => {
        if (!base) return [];
        const items = [];
        let nextKey = null;
        do {
            const res = await http.get(RULES_PATH, {
                params: { limit, ...(nextKey ? { nextKey: JSON.stringify(nextKey) } : {}) },
            });
            const page = res?.data || {};
            items.push(...(page.items || []));
            nextKey = page.nextKey || null;
        } while (nextKey);
        return items;
    };

    // Upsert in bulk; accepts array of { Code, Label, Status }
    const upsertMany = async (rules) => {
        if (!base || !rules?.length) return;
        await http.post(RULES_PATH, rules);
    };

    const deleteOne = async (code) => {
        if (!base || !code) return;
        await http.delete(RULES_PATH, { data: { Code: code } });
    };

    return { listAll, upsertMany, deleteOne };
}

// ✨ Add tenantId to inject x-tenant-id header
function makeAssociationsBackend(backEndUrl, tenantId) {
    const base = (backEndUrl || "").replace(/\/+$/, "");
    const baseHeaders = { "Content-Type": "application/json", Accept: "application/json" };
    const http = axios.create({
        baseURL: base,
        timeout: 20000,
        headers: { ...baseHeaders, ...(tenantId ? { "x-tenant-id": tenantId } : {}) },
    });

    // If courseId is supplied, server should filter; otherwise return all (if supported).
    const listAll = async ({ courseId, limit = 200 } = {}) => {
        if (!base) return [];
        const items = [];
        let nextKey = null;
        do {
            const params = {
                limit,
                ...(courseId ? { courseId } : {}),
                ...(nextKey ? { nextKey: JSON.stringify(nextKey) } : {}),
            };
            const res = await http.get(LPA_PATH, { params });
            const page = res?.data || {};
            items.push(...(page.items || []));
            nextKey = page.nextKey || null;
        } while (nextKey);
        return items;
    };

    return { listAll };
}

/** ---------------- Learn Roles Helper ---------------- */
async function fetchInstitutionRoles(platformUrl, token) {
    const base = (platformUrl || "").replace(/\/+$/, "");
    if (!base || !token) return [];
    const http = axios.create({
        baseURL: base,
        timeout: 20000,
        headers: { Authorization: `Bearer ${token}` },
    });
    const { data } = await http.get("/learn/api/public/v1/users/me");
    return data?.institutionRoleIds || [];
}

/** ---------------- Component ---------------- */
// ✨ Added tenantId prop (optional)
const LatePenaltyRulesAdmin = ({ backEndUrl, platformUrl, coursePk1, locale = "en_US", tenantId }) => {
    const M = useMemo(() => getMessages(locale), [locale]);
    // ✨ Pass tenantId through to backends
    const api = useMemo(() => makeRulesBackend(backEndUrl, tenantId), [backEndUrl, tenantId]);
    const assocApi = useMemo(() => makeAssociationsBackend(backEndUrl, tenantId), [backEndUrl, tenantId]);
    const location = useLocation();
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const authToken = queryParams.get("token");

    // ----- Institution role gate -----
    const [roleChecked, setRoleChecked] = useState(false);
    const [hasRequiredRole, setHasRequiredRole] = useState(false);
    const REQUIRED_ROLE = "LATE_PENALTY_ADMIN";

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const roles = await fetchInstitutionRoles(platformUrl, authToken);
                if (!cancelled) {
                    setHasRequiredRole(Array.isArray(roles) && roles.includes(REQUIRED_ROLE));
                    setRoleChecked(true);
                }
            } catch (e) {
                console.warn("Role check failed:", e);
                if (!cancelled) {
                    setHasRequiredRole(false);
                    setRoleChecked(true);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [platformUrl, authToken]);

    // ----- Manage Rules state -----
    const [rows, setRows] = useState([]);
    const [serverRows, setServerRows] = useState([]);
    const [status, setStatus] = useState({ loading: false, error: null, toast: "" });
    const [sortKey, setSortKey] = useState("Label"); // default now Label
    const [sortDir, setSortDir] = useState("asc");
    const [filter, setFilter] = useState("");

    // ----- Tabs -----
    const TABS = [t(M, "tabs.manage"), t(M, "tabs.view")];
    const [activeTab, setActiveTab] = useState(t(M, "tabs.manage"));

    // ----- View Penalties state (from associations backend) -----
    const [penRows, setPenRows] = useState([]); // raw association items
    const [penStatus, setPenStatus] = useState({ loading: false, error: null });
    // Aggregated + enriched rows
    const [enrichedRows, setEnrichedRows] = useState([]);
    const [vpSortKey, setVpSortKey] = useState("courseId");
    const [vpSortDir, setVpSortDir] = useState("asc");
    const [vpFilter, setVpFilter] = useState("");

    // Load rules after gate passes
    useEffect(() => {
        if (!roleChecked || !hasRequiredRole) return;
        let cancelled = false;
        (async () => {
            setStatus((s) => ({ ...s, loading: true, error: null }));
            try {
                const items = await api.listAll();
                const normalized = (items || []).map(toUiRow);
                if (!cancelled) {
                    setRows(normalized);
                    setServerRows(normalized.map((r) => ({ ...r })));
                    setStatus((s) => ({ ...s, loading: false }));
                }
            } catch (e) {
                if (!cancelled) {
                    setStatus({ loading: false, error: friendlyErr(e, M), toast: "" });
                }
            }
        })();
        return () => { cancelled = true; };
    }, [api, M, roleChecked, hasRequiredRole]);

    // Auto-fetch penalties when the tab opens the first time (after gate)
    useEffect(() => {
        if (!roleChecked || !hasRequiredRole) return;
        if (activeTab === t(M, "tabs.view") && penRows.length === 0 && !penStatus.loading) {
            void fetchPenalties();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, roleChecked, hasRequiredRole]);

    /** ---------- Helpers (shared) ---------- */
    function toUiRow(it) {
        const code = it?.Code ?? "";
        const r = {
            _uid: cryptoUid(),
            Code: code,
            Label: it?.Label ?? "",
            Status: it?.Status ?? "Active",
            _isNew: false,
            _dirty: false,
            _errors: {},
            _codeAutogenerated: !code?.trim(),
        };
        if (!r.Code && r.Label) {
            r.Code = slugify(r.Label);
            r._codeAutogenerated = true;
        }
        return r;
    }

    const cryptoUid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
    const isFiniteNumber = (v) => Number.isFinite(Number(v));
    const friendlyErr = (e, Mx) => e?.response?.data?.message || e?.message || t(Mx, "errors.somethingWrong");
    const normalizeBase = (u) => (u || "").replace(/\/+$/, "");

    const setToast = (msg) => {
        setStatus((s) => ({ ...s, toast: msg }));
        window.clearTimeout(setToast._t);
        setToast._t = window.setTimeout(() => {
            setStatus((s) => ({ ...s, toast: "" }));
        }, 3000);
    };

    const resetToServer = () => {
        setRows(
            serverRows.map((r) => ({
                ...r,
                _dirty: false,
                _isNew: false,
                _errors: {},
                _codeAutogenerated: !r.Code?.trim(),
            }))
        );
        setToast(t(M, "toasts.reverted"));
    };

    /** ---------- Slug / Uniqueness helpers for Code (hidden) ---------- */
    const slugify = (s) => {
        if (!s) return "";
        const cleaned = s.replace(/&/g, " and ");
        return cleaned
            .toLowerCase()
            .normalize("NFKD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "")
            .replace(/-{2,}/g, "-")
            .slice(0, 60);
    };

    const makeUniqueCode = (base, allRows, selfIdx) => {
        let code = base || "";
        const taken = new Set(
            allRows
                .filter((_, i) => i !== selfIdx)
                .map((r) => (r.Code || "").trim().toLowerCase())
        );
        if (!code) return "";
        let candidate = code;
        let n = 2;
        while (taken.has(candidate.toLowerCase())) {
            candidate = `${code}-${n}`;
            n += 1;
        }
        return candidate;
    };

    /** ---------- Rule Label Validation ---------- */
    const RX_PERCENT_THRESHOLD =
        /^(\d+(?:\.\d+)?)\s*%\s*penalty\s*if\s*more\s*than\s*(\d+(?:\.\d+)?)\s*(hour|hours|day|days)\s*late$/i;
    const RX_POINTS_THRESHOLD =
        /^(\d+(?:\.\d+)?)\s*points?\s*penalty\s*if\s*more\s*than\s*(\d+(?:\.\d+)?)\s*(hour|hours|day|days)\s*late$/i;
    const RX_PERCENT_PER_DAY = /^(\d+(?:\.\d+)?)\s*%\s*penalty\s*each\s*day\s*late$/i;
    const RX_POINTS_PER_DAY_CAP =
        /^(\d+(?:\.\d+)?)\s*points?\s*penalty\s*each\s*day\s*late(?:\s*(?:for\s*a\s*(?:max|maximum)\s*of|max(?:imum)?)\s*(\d+(?:\.\d+)?)\s*points?)?$/i;

    function parseRuleLabelForValidation(label) {
        const txt = String(label || "").trim();
        if (!txt) return { ok: false, reason: t(M, "errors.required") };

        let m = txt.match(RX_PERCENT_THRESHOLD);
        if (m) {
            const amount = parseFloat(m[1]); // %
            const threshold = parseFloat(m[2]);
            const unit = m[3].toLowerCase().startsWith("hour") ? "hours" : "days";
            if (!(amount > 0 && amount <= 100))
                return { ok: false, reason: t(M, "errors.percentRange") };
            if (!(threshold > 0)) return { ok: false, reason: t(M, "errors.thresholdGt0") };
            return { ok: true, type: "percent_threshold", amount, threshold, unit };
        }

        m = txt.match(RX_POINTS_THRESHOLD);
        if (m) {
            const points = parseFloat(m[1]);
            const threshold = parseFloat(m[2]);
            const unit = m[3].toLowerCase().startsWith("hour") ? "hours" : "days";
            if (!(points > 0)) return { ok: false, reason: t(M, "errors.pointsGt0") };
            if (!(threshold > 0)) return { ok: false, reason: t(M, "errors.thresholdGt0") };
            return { ok: true, type: "points_threshold", points, threshold, unit };
        }

        m = txt.match(RX_PERCENT_PER_DAY);
        if (m) {
            const amount = parseFloat(m[1]); // %
            if (!(amount > 0 && amount <= 100))
                return { ok: false, reason: t(M, "errors.percentRange") };
            return { ok: true, type: "percent_per_day", amount };
        }

        m = txt.match(RX_POINTS_PER_DAY_CAP);
        if (m) {
            const rate = parseFloat(m[1]); // points/day
            const cap = m[2] !== undefined ? parseFloat(m[2]) : null;
            if (!(rate > 0)) return { ok: false, reason: t(M, "errors.ppdGt0") };
            if (cap !== null && !(cap > 0)) return { ok: false, reason: t(M, "errors.maxGt0") };
            if (cap !== null && cap < rate) return { ok: false, reason: t(M, "errors.maxGeRate") };
            return { ok: true, type: "points_per_day", rate, cap };
        }

        return { ok: false, reason: t(M, "errors.unrecognized") };
    }

    /** ---------- CRUD Actions (Manage Rules) ---------- */
    const markDirty = (idx, patch) => {
        setRows((prev) => {
            const next = prev.slice();
            const cur = { ...next[idx], ...patch };
            cur._dirty = true;
            next[idx] = cur;
            return next;
        });
    };

    const onLabelChange = (idx, newLabel) => {
        setRows((prev) => {
            const next = prev.slice();
            const cur = { ...next[idx] };
            cur.Label = newLabel;
            cur._dirty = true;
            const base = slugify(newLabel);
            cur.Code = makeUniqueCode(base, next, idx);
            cur._codeAutogenerated = true;

            const v = parseRuleLabelForValidation(newLabel);
            cur._errors = { ...cur._errors, Label: v.ok ? undefined : v.reason, Code: undefined };

            next[idx] = cur;
            return next;
        });
    };

    const validateRow = (row, allRows, idx) => {
        const errs = {};
        if (!row.Label.trim()) {
            errs.Label = t(M, "errors.required");
        } else {
            const v = parseRuleLabelForValidation(row.Label);
            if (!v.ok) errs.Label = v.reason || t(M, "errors.unrecognized");
        }
        const code = (row.Code || "").trim();
        if (!code) errs.Label = errs.Label || t(M, "errors.required");
        const duplicate = allRows.findIndex(
            (r, i) => i !== idx && (r.Code || "").trim().toLowerCase() === code.toLowerCase()
        );
        if (duplicate >= 0) errs.Label = errs.Label || t(M, "errors.unrecognized");
        const st = (row.Status || "").trim();
        if (!/^(Active|Inactive)$/i.test(st)) errs.Status = t(M, "errors.unrecognized");
        return errs;
    };

    const addNewRow = () => {
        setRows((prev) => [
            ...prev,
            {
                _uid: cryptoUid(),
                Code: "",
                Label: "",
                Status: "Active",
                _isNew: true,
                _dirty: true,
                _errors: {},
                _codeAutogenerated: true,
            },
        ]);
    };

    const removeRow = async (idx) => {
        const row = rows[idx];
        if (row._isNew) {
            setRows((prev) => prev.filter((_, i) => i !== idx));
            return;
        }
        try {
            await api.deleteOne(row.Code);
            const next = rows.filter((_, i) => i !== idx);
            setRows(next);
            setServerRows(next.map((r) => ({ ...r, _dirty: false, _isNew: false, _errors: {} })));
            setToast(t(M, "toasts.deleted"));
        } catch (e) {
            setStatus((s) => ({ ...s, error: friendlyErr(e, M) }));
        }
    };

    const ensureCodesBeforeValidate = (list) =>
        list.map((r, i, all) => {
            if (!r.Label?.trim()) return r;
            const base = slugify(r.Label);
            const gen = makeUniqueCode(base, all, i);
            if (r.Code !== gen) return { ...r, Code: gen, _codeAutogenerated: true };
            return r;
        });

    const saveAll = async () => {
        const withCodes = ensureCodesBeforeValidate(rows);
        setRows(withCodes);
        const validated = withCodes.map((r, i) => ({ idx: i, errs: validateRow(r, withCodes, i) }));
        const anyErr = validated.some((v) => Object.keys(v.errs).length > 0);
        if (anyErr) {
            setRows((prev) => {
                const next = prev.slice();
                validated.forEach(({ idx, errs }) => (next[idx] = { ...next[idx], _errors: errs }));
                return next;
            });
            setStatus((s) => ({ ...s, error: t(M, "errors.genericFix") }));
            return;
        }
        const payload = withCodes.map((r) => ({
            Code: r.Code.trim(),
            Label: r.Label.trim(),
            Status: r.Status || "Active",
        }));
        try {
            await api.upsertMany(payload);
            const normalized = payload.map((p) => toUiRow({ ...p }));
            setServerRows(normalized);
            setRows(normalized);
            setStatus((s) => ({ ...s, error: null }));
            setToast(t(M, "toasts.saved"));
        } catch (e) {
            setStatus((s) => ({ ...s, error: friendlyErr(e, M) }));
        }
    };

    /** ---------- Sorting & Filtering (Manage Rules) ---------- */
    const toggleSort = (key) => {
        setSortKey((prev) => {
            if (prev === key) {
                setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                return prev;
            }
            setSortDir("asc");
            return key;
        });
    };
    const ariaSort = (key) =>
        sortKey === key ? (sortDir === "asc" ? "ascending" : "descending") : "none";
    const sortGlyph = (key) => (sortKey !== key ? "↕" : sortDir === "asc" ? "▲" : "▼");

    const visibleRows = useMemo(() => {
        const term = (filter || "").trim().toLowerCase();
        let list = rows.slice();
        if (term) {
            list = list.filter(
                (r) =>
                    r.Label.toLowerCase().includes(term) ||
                    (r.Status || "").toLowerCase().includes(term)
            );
        }
        const dir = sortDir === "asc" ? 1 : -1;
        list.sort((a, b) => {
            const A = (a[sortKey] ?? "").toString().toLowerCase();
            const B = (b[sortKey] ?? "").toString().toLowerCase();
            if (A < B) return -1 * dir;
            if (A > B) return 1 * dir;
            return 0;
        });
        return list;
    }, [rows, filter, sortKey, sortDir]);

    /** ---------- View Penalties: fetch from associations + enrich ---------- */
    const authHeaders = useMemo(() => {
        const h = { Accept: "application/json" };
        if (authToken) h["Authorization"] = `Bearer ${authToken}`;
        return h;
    }, [authToken]);

    // Helper: build a Blackboard-style ID from a numeric PK1 (for internal lookups only)
    const toBbId = (pk1) => {
        const n = String(pk1 || "").trim();
        return n ? `_${n}_1` : "";
    };

    async function fetchPenalties() {
        if (!hasRequiredRole) return;
        setPenStatus({ loading: true, error: null });
        try {
            // Resolve external courseId for backend filtering, if a course pk1 supplied
            let courseIdForBackend = null;
            const httpPlatform = axios.create({
                baseURL: normalizeBase(platformUrl),
                timeout: 20000,
                headers: { ...authHeaders, Accept: "application/json" },
            });
            if (coursePk1) {
                try {
                    const info = await lookupCourse(httpPlatform, String(coursePk1));
                    courseIdForBackend = info?.courseId || null;
                } catch {
                    /* noop */
                }
            }

            // Pull association items
            let assocItems = [];
            if (courseIdForBackend) {
                assocItems = await assocApi.listAll({ courseId: courseIdForBackend });
                if (!assocItems?.length) {
                    const candidates = [String(coursePk1 || ""), toBbId(String(coursePk1 || ""))].filter(Boolean);
                    for (const cid of candidates) {
                        const got = await assocApi.listAll({ courseId: cid });
                        if (got?.length) {
                            assocItems = got;
                            break;
                        }
                    }
                }
            } else {
                assocItems = await assocApi.listAll(); // if backend supports global list
            }
            setPenRows(assocItems || []);

            // Flatten PenaltiesBy and aggregate by courseId|userId|contentItemId|ruleCode; capture override flag
            const flattened = [];
            for (const it of assocItems || []) {
                const courseId = it?.CourseId ?? it?.courseId ?? "";
                const contentItemId = it?.ContentItemId ?? it?.contentItemId ?? "";
                const ruleCode = it?.Rule ?? it?.rule ?? "";
                const penMap = it?.PenaltiesBy ?? it?.Penalties ?? it?.penaltiesBy ?? {};
                const ovMap = it?.Overrides ?? it?.overrides ?? it?.OverridesBy ?? {};
                if (!courseId || !contentItemId) continue;
                for (const [userId, points] of Object.entries(penMap)) {
                    const n = Number(points);
                    if (!Number.isFinite(n)) continue;
                    const overridden = ovMap && Object.prototype.hasOwnProperty.call(ovMap, userId);
                    flattened.push({ courseId, contentItemId, userId, ruleCode, points: n, overridden: !!overridden });
                }
            }
            const grouped = aggregateByCourseUserAssignmentRule(flattened);

            // Enrich (course name, student, assignment title, rule label, term)
            const enriched = await enrichAggRows(grouped, httpPlatform);
            setEnrichedRows(enriched);
            setPenStatus({ loading: false, error: null });
        } catch (e) {
            setPenStatus({ loading: false, error: friendlyErr(e, M) });
        }
    }

    function aggregateByCourseUserAssignmentRule(rowsIn) {
        // Key: `${courseId}|${userId}|${contentItemId}|${ruleCode}`
        const map = new Map();
        for (const r of rowsIn) {
            const c = String(r.courseId || "");
            const u = String(r.userId || "");
            const a = String(r.contentItemId || "");
            const rule = String(r.ruleCode || "");
            if (!c || !u || !a) continue;
            const key = `${c}|${u}|${a}|${rule}`;
            const prev =
                map.get(key) || { courseId: c, userId: u, contentItemId: a, ruleCode: rule, points: 0, overridden: false };
            prev.points += Number(r.points) || 0;
            prev.overridden = prev.overridden || !!r.overridden;
            map.set(key, prev);
        }
        return Array.from(map.values());
    }

    // Best-effort course lookup by id/pk1/bbid (now also returns termId)
    async function lookupCourse(http, coursePk1OrId) {
        const pk1 = String(coursePk1OrId || "").trim();
        if (!pk1) return { courseId: "", courseName: "", _bbid: "", termId: "" };

        const idBbid = encodeURIComponent(toBbId(pk1));
        const idRaw = encodeURIComponent(pk1);
        const candidates = [
            `/learn/api/public/v1/courses/${idBbid}`,
            `/learn/api/public/v1/courses/${idRaw}`,
            `/learn/api/public/v2/courses/${idBbid}`,
            `/learn/api/public/v2/courses/${idRaw}`,
        ];

        for (const url of candidates) {
            try {
                const res = await http.get(url);
                const data = res?.data || {};
                const courseId = data?.courseId || data?.id || pk1;
                const name = data?.name || data?.courseName || "";
                const bbid = data?.id || data?.uuid || toBbId(pk1);
                const termId = data?.termId || data?.term?.id || "";
                return { courseId, courseName: name, _bbid: bbid, termId: termId || "" };
            } catch {
                // keep trying
            }
        }
        return { courseId: pk1, courseName: "", _bbid: toBbId(pk1), termId: "" };
    }

    // Term lookup to get display name
    async function lookupTerm(http, termId) {
        const tId = String(termId || "").trim();
        if (!tId) return { termName: "" };
        const enc = encodeURIComponent(tId);
        const candidates = [
            `/learn/api/public/v1/terms/${enc}`,
            `/learn/api/public/v2/terms/${enc}`,
        ];
        for (const url of candidates) {
            try {
                const res = await http.get(url);
                const d = res?.data || {};
                const name = d?.name || d?.title || d?.displayName || "";
                return { termName: name || "" };
            } catch {
                // keep trying
            }
        }
        return { termName: "" };
    }

    // Best-effort user lookup by id/pk1/bbid
    async function lookupUser(http, userPk1OrId) {
        const pk1 = String(userPk1OrId || "").trim();
        if (!pk1) return { firstName: "", lastName: "" };

        const idBbid = encodeURIComponent(toBbId(pk1));
        const idRaw = encodeURIComponent(pk1);
        const candidates = [
            `/learn/api/public/v1/users/${idBbid}`,
            `/learn/api/public/v1/users/${idRaw}`,
            `/learn/api/public/v2/users/${idBbid}`,
            `/learn/api/public/v2/users/${idRaw}`,
        ];

        for (const url of candidates) {
            try {
                const res = await http.get(url);
                const u = res?.data || {};
                const first = u?.name?.given || u?.givenName || u?.firstName || "";
                const last = u?.name?.family || u?.familyName || u?.lastName || "";
                return { firstName: first, lastName: last };
            } catch {
                // keep trying
            }
        }
        return { firstName: "", lastName: "" };
    }

    // Best-effort content lookup to get assignment title
    async function lookupContentTitle(http, courseId, contentId) {
        if (!courseId || !contentId) return { title: "" };
        const cId = encodeURIComponent(courseId);
        const cnt = encodeURIComponent(contentId);
        const candidates = [
            `/learn/api/public/v1/courses/${cId}/contents/${cnt}`,
            `/learn/api/public/v2/courses/${cId}/contents/${cnt}`,
        ];
        for (const url of candidates) {
            try {
                const res = await http.get(url);
                const c = res?.data || {};
                const title = c?.title || c?.name || "";
                return { title };
            } catch {
                // keep trying
            }
        }
        return { title: "" };
    }

    async function enrichAggRows(aggRows, http) {
        if (!aggRows.length) return [];
        // Build maps for lookups
        const uniqueCourseIds = Array.from(new Set(aggRows.map((r) => String(r.courseId))));
        const uniqueUserIds = Array.from(new Set(aggRows.map((r) => String(r.userId))));
        const uniqueContentPairs = Array.from(
            new Set(aggRows.map((r) => `${r.courseId}|${r.contentItemId}`))
        );

        // Lookups (best-effort)
        let courseMap = new Map();
        let userMap = new Map();
        let contentMap = new Map();
        let termMap = new Map();

        if (platformUrl && authHeaders?.Authorization) {
            // Courses (also capture termId)
            const courseLookups = await Promise.all(
                uniqueCourseIds.map(async (cid) => {
                    const out = await lookupCourse(http, cid);
                    return { id: cid, ...out }; // includes termId
                })
            );

            // Terms (unique termIds from courses)
            const uniqueTermIds = Array.from(
                new Set(courseLookups.map((c) => c.termId).filter(Boolean))
            );
            const termLookups = await Promise.all(
                uniqueTermIds.map(async (tid) => {
                    const out = await lookupTerm(http, tid);
                    return { id: tid, ...out };
                })
            );

            // Users
            const userLookups = await Promise.all(
                uniqueUserIds.map(async (uid) => {
                    const out = await lookupUser(http, uid);
                    return { id: uid, ...out };
                })
            );

            // Content titles
            const contentLookups = await Promise.all(
                uniqueContentPairs.map(async (pair) => {
                    const [cid, contentId] = pair.split("|");
                    const out = await lookupContentTitle(http, cid, contentId);
                    return { key: pair, title: out?.title || "" };
                })
            );

            courseMap = new Map(
                courseLookups.map((c) => [
                    String(c.id),
                    { courseId: c.courseId || c.id, courseName: c.courseName || "", termId: c.termId || "" },
                ])
            );
            termMap = new Map(termLookups.map((t) => [String(t.id), t.termName || ""]));
            userMap = new Map(
                userLookups.map((u) => [String(u.id), { firstName: u.firstName || "", lastName: u.lastName || "" }])
            );
            contentMap = new Map(contentLookups.map((x) => [x.key, x.title || ""]));
        }

        const ruleLabelByCode = new Map(rows.map((r) => [r.Code, r.Label]));

        return aggRows.map((row) => {
            const cm =
                courseMap.get(String(row.courseId)) || { courseId: row.courseId, courseName: "", termId: "" };
            const termName = cm.termId ? (termMap.get(String(cm.termId)) || "") : "";
            const um = userMap.get(String(row.userId)) || { firstName: "", lastName: "" };
            const title = contentMap.get(`${row.courseId}|${row.contentItemId}`) || "";
            const ruleLabel = ruleLabelByCode.get(row.ruleCode) || row.ruleCode || "";
            return {
                ...row,
                courseId: cm.courseId,
                courseName: cm.courseName,
                termId: cm.termId || "",
                termName,
                firstName: um.firstName,
                lastName: um.lastName,
                assignmentTitle: title,
                ruleLabel,
            };
        });
    }

    const visiblePenalties = useMemo(() => {
        // Filter by Term, Course ID, Course Name, Student, Assignment, Rule (case-insensitive)
        const term = (vpFilter || "").trim().toLowerCase();
        let list = enrichedRows.slice();
        if (term) {
            list = list.filter((r) => {
                const termName = (r.termName || "").toLowerCase();
                const courseId = (r.courseId || "").toLowerCase();
                const courseName = (r.courseName || "").toLowerCase();
                const student = `${r.firstName || ""} ${r.lastName || ""}`.trim().toLowerCase();
                const asmt = (r.assignmentTitle || "").toLowerCase();
                const rule = (r.ruleLabel || "").toLowerCase();
                return (
                    termName.includes(term) ||
                    courseId.includes(term) ||
                    courseName.includes(term) ||
                    student.includes(term) ||
                    asmt.includes(term) ||
                    rule.includes(term)
                );
            });
        }
        // Sort
        const dir = vpSortDir === "asc" ? 1 : -1;
        list.sort((a, b) => {
            let A, B;
            if (vpSortKey === "term") {
                A = (a.termName || "").toLowerCase();
                B = (b.termName || "").toLowerCase();
            } else if (vpSortKey === "courseId") {
                A = (a.courseId || "").toLowerCase();
                B = (b.courseId || "").toLowerCase();
            } else if (vpSortKey === "courseName") {
                A = (a.courseName || "").toLowerCase();
                B = (b.courseName || "").toLowerCase();
            } else if (vpSortKey === "student") {
                A = `${a.lastName || ""}, ${a.firstName || ""}`.toLowerCase();
                B = `${b.lastName || ""}, ${b.firstName || ""}`.toLowerCase();
            } else if (vpSortKey === "assignment") {
                A = (a.assignmentTitle || "").toLowerCase();
                B = (b.assignmentTitle || "").toLowerCase();
            } else if (vpSortKey === "rule") {
                A = (a.ruleLabel || "").toLowerCase();
                B = (b.ruleLabel || "").toLowerCase();
            } else if (vpSortKey === "override") {
                A = a.overridden ? 1 : 0;
                B = b.overridden ? 1 : 0;
            } else {
                // points
                A = Number(a.points) || 0;
                B = Number(b.points) || 0;
            }
            if (A < B) return -1 * dir;
            if (A > B) return 1 * dir;
            return 0;
        });
        return list;
    }, [enrichedRows, vpFilter, vpSortKey, vpSortDir]);

    const toggleVpSort = (key) => {
        setVpSortKey((prev) => {
            if (prev === key) {
                setVpSortDir((d) => (d === "asc" ? "desc" : "asc"));
                return prev;
            }
            setVpSortDir("asc");
            return key;
        });
    };
    const vpAriaSort = (key) =>
        vpSortKey === key ? (vpSortDir === "asc" ? "ascending" : "descending") : "none";
    const vpGlyph = (key) => (vpSortKey !== key ? "↕" : vpSortDir === "asc" ? "▲" : "▼");

    /** ---------- CSV Download (filtered view) ---------- */
    const escapeCsv = (v) => {
        if (v === null || v === undefined) return '""';
        const s = String(v);
        return `"${s.replace(/"/g, '""')}"`;
    };
    const handleDownloadCsv = () => {
        const header = [
            t(M, "penalties.headers.term"),
            t(M, "penalties.headers.courseId"),
            t(M, "penalties.headers.courseName"),
            t(M, "penalties.headers.studentFirst"),
            t(M, "penalties.headers.studentLast"),
            t(M, "penalties.headers.student"),
            t(M, "penalties.headers.assignment"),
            t(M, "penalties.headers.rule"),
            t(M, "penalties.headers.override"),
            t(M, "penalties.headers.points"),
        ];
        const lines = [header.map(escapeCsv).join(",")];

        for (const r of visiblePenalties) {
            const studentFull = `${r.firstName || ""} ${r.lastName || ""}`.trim() || r.userId || "";
            const row = [
                r.termName || "",
                r.courseId || "",
                r.courseName || "",
                r.firstName || "",
                r.lastName || "",
                studentFull,
                r.assignmentTitle || "",
                r.ruleLabel || r.ruleCode || "",
                r.overridden ? t(M, "bool.yes") : t(M, "bool.no"),
                Number.isFinite(r.points) ? r.points : 0,
            ];
            lines.push(row.map(escapeCsv).join(","));
        }

        const csv = "\uFEFF" + lines.join("\n"); // BOM for Excel
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const ts = new Date().toISOString().replace(/:/g, "-").replace(/\..*$/, "");
        a.href = url;
        a.download = `late-penalties_${ts}.csv`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
            URL.revokeObjectURL(url);
            document.body.removeChild(a);
        }, 0);
    };

    /** ---------- Gate-first rendering ---------- */
    if (!roleChecked) {
        return (
            <div className="bb-container" style={{ padding: 16 }}>
                {t(M, "gate.checking")}
            </div>
        );
    }

    if (!hasRequiredRole) {
        return (
            <div className="bb-container" style={{ padding: 16 }}>
                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 16 }}>
                    <h2 style={h2Style}>{t(M, "gate.blockedTitle")}</h2>
                    <p style={{ margin: 0 }}>{t(M, "gate.blockedMsg")}</p>
                </div>
            </div>
        );
    }

    /** ---------- Render (after gate) ---------- */
    if (status.loading) {
        return <div className="bb-container" style={{ padding: 16 }}>{t(M, "loading.rules")}</div>;
    }

    return (
        <div className="bb-container" style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
            {/* Tabs */}
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                {TABS.map((tName) => {
                    const active = activeTab === tName;
                    return (
                        <button
                            key={tName}
                            type="button"
                            onClick={() => setActiveTab(tName)}
                            style={{ ...tabBtn, ...(active ? tabBtnActive : tabBtnInactive) }}
                            aria-pressed={active}
                        >
                            {tName}
                        </button>
                    );
                })}
            </div>

            {activeTab === t(M, "tabs.manage") ? (
                <section className="bb-card" style={cardStyle}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: "wrap",
                        }}
                    >
                        <h2 style={h2Style}>{t(M, "headings.adminTitle")}</h2>
                        <div style={{ display: "flex", gap: 8 }}>
                            <button type="button" onClick={saveAll} style={primaryBtn}>
                                {t(M, "buttons.saveAll")}
                            </button>
                            <button type="button" onClick={resetToServer} style={secondaryBtn}>
                                {t(M, "buttons.revert")}
                            </button>
                            <button type="button" onClick={addNewRow} style={quietBtn}>
                                {t(M, "buttons.addRule")}
                            </button>
                        </div>
                    </div>

                    {status.error && <div style={errorBoxStyle}>{status.error}</div>}
                    {status.toast && <div style={toastStyle}>{status.toast}</div>}

                    <div
                        style={{
                            margin: "12px 0",
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                            flexWrap: "wrap",
                        }}
                    >
                        <input
                            type="text"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder={t(M, "filters.rulesPlaceholder")}
                            style={inputStyle}
                        />
                        <span style={{ fontSize: 12, color: "#666" }}>
                            {t(M, "filters.showing", { current: String(visibleRows.length), total: String(rows.length) })}
                        </span>
                    </div>

                    <div style={{ margin: "8px 0 12px", fontSize: 12, color: "#4b5563" }}>
                        <strong>{t(M, "headings.validFormats")}</strong>: {t(M, "examples.ruleFormats")}
                    </div>

                    <table className="bb-table" style={listTableStyle}>
                        <thead>
                        <tr>
                            <th style={{ width: "12%" }}>{t(M, "table.actions")}</th>
                            <th
                                style={{ width: "58%", cursor: "pointer", userSelect: "none" }}
                                role="button"
                                tabIndex={0}
                                aria-sort={ariaSort("Label")}
                                onClick={() => toggleSort("Label")}
                                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggleSort("Label")}
                                title={t(M, "table.sortByLabel")}
                            >
                                {t(M, "table.label")} <span style={sortIconStyle}>{sortGlyph("Label")}</span>
                            </th>
                            <th
                                style={{ width: "30%", cursor: "pointer", userSelect: "none" }}
                                role="button"
                                tabIndex={0}
                                aria-sort={ariaSort("Status")}
                                onClick={() => toggleSort("Status")}
                                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggleSort("Status")}
                                title={t(M, "table.sortByStatus")}
                            >
                                {t(M, "table.status")} <span style={sortIconStyle}>{sortGlyph("Status")}</span>
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        {visibleRows.length === 0 ? (
                            <tr>
                                <td colSpan={3} style={{ textAlign: "center" }}>
                                    {t(M, "table.noRules")}
                                </td>
                            </tr>
                        ) : (
                            visibleRows.map((r) => {
                                const idx = rows.findIndex((x) => x._uid === r._uid);
                                const errs = r._errors || {};
                                return (
                                    <tr key={r._uid}>
                                        <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                                            <button
                                                type="button"
                                                onClick={() => removeRow(idx)}
                                                style={iconBtn}
                                                title={t(M, "buttons.deleteRule")}
                                                aria-label={t(M, "buttons.deleteRule")}
                                            >
                                                ❌
                                            </button>
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                value={r.Label}
                                                onChange={(e) => onLabelChange(idx, e.target.value)}
                                                style={{
                                                    ...inputStyle,
                                                    borderColor: errs.Label ? "#dc2626" : inputStyle.borderColor,
                                                }}
                                                aria-label={t(M, "table.label")}
                                                placeholder={t(M, "field.labelPlaceholder")}
                                            />
                                            {errs.Label && <FieldError>{errs.Label}</FieldError>}
                                        </td>
                                        <td>
                                            <select
                                                value={r.Status || "Active"}
                                                onChange={(e) =>
                                                    markDirty(idx, {
                                                        Status: e.target.value,
                                                        _errors: { ...errs, Status: undefined },
                                                    })
                                                }
                                                style={{
                                                    ...selectStyle,
                                                    borderColor: errs.Status ? "#dc2626" : selectStyle.borderColor,
                                                }}
                                                aria-label={t(M, "field.status")}
                                            >
                                                <option value="Active">{t(M, "status.active")}</option>
                                                <option value="Inactive">{t(M, "status.inactive")}</option>
                                            </select>
                                            {errs.Status && <FieldError>{errs.Status}</FieldError>}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                        </tbody>
                    </table>

                    <div style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        <button type="button" onClick={saveAll} style={primaryBtn}>
                            {t(M, "buttons.saveAll")}
                        </button>
                        <button type="button" onClick={addNewRow} style={quietBtn}>
                            {t(M, "buttons.addRule")}
                        </button>
                    </div>
                </section>
            ) : (
                <section className="bb-card" style={cardStyle}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: "wrap",
                        }}
                    >
                        <h2 style={h2Style}>{t(M, "headings.viewPenalties")}</h2>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                            {typeof coursePk1 !== "undefined" && (
                                <span style={{ fontSize: 12, color: "#4b5563" }}>
                                    {t(M, "filters.courseFilter")} <strong>{String(coursePk1)}</strong>
                                </span>
                            )}
                            <button type="button" onClick={fetchPenalties} style={secondaryBtn}>
                                {t(M, "buttons.refresh")}
                            </button>
                            <button
                                type="button"
                                onClick={handleDownloadCsv}
                                style={secondaryBtn}
                                disabled={!visiblePenalties.length}
                                title={t(M, "buttons.downloadCsvTooltip")}
                            >
                                {t(M, "buttons.downloadCsv")}
                            </button>
                        </div>
                    </div>

                    {penStatus.error && <div style={errorBoxStyle}>{penStatus.error}</div>}
                    {penStatus.loading && <div style={{ marginTop: 8 }}>{t(M, "loading.penalties")}</div>}

                    <div
                        style={{
                            marginTop: 12,
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                            flexWrap: "wrap",
                        }}
                    >
                        <input
                            type="text"
                            value={vpFilter}
                            onChange={(e) => setVpFilter(e.target.value)}
                            placeholder={t(M, "filters.penaltiesPlaceholder")}
                            style={inputStyle}
                        />
                        <span style={{ fontSize: 12, color: "#666" }}>
                            {t(M, "penalties.rowsCount", { count: String(enrichedRows.length) })}
                        </span>
                    </div>

                    {/* Aggregated & enriched table */}
                    <table style={{ ...listTableStyle, marginTop: 12 }}>
                        <thead>
                        <tr>
                            <ThButton
                                width="14%"
                                ariaSort={vpAriaSort("term")}
                                onClick={() => toggleVpSort("term")}
                                title={t(M, "table.sortByTerm")}
                            >
                                {t(M, "penalties.headers.term")} {vpGlyph("term")}
                            </ThButton>
                            <ThButton
                                width="14%"
                                ariaSort={vpAriaSort("courseId")}
                                onClick={() => toggleVpSort("courseId")}
                                title={t(M, "table.sortByCourseId")}
                            >
                                {t(M, "penalties.headers.courseId")} {vpGlyph("courseId")}
                            </ThButton>
                            <ThButton
                                width="18%"
                                ariaSort={vpAriaSort("courseName")}
                                onClick={() => toggleVpSort("courseName")}
                                title={t(M, "table.sortByCourseName")}
                            >
                                {t(M, "penalties.headers.courseName")} {vpGlyph("courseName")}
                            </ThButton>
                            <ThButton
                                width="20%"
                                ariaSort={vpAriaSort("student")}
                                onClick={() => toggleVpSort("student")}
                                title={t(M, "table.sortByStudent")}
                            >
                                {t(M, "penalties.headers.student")} {vpGlyph("student")}
                            </ThButton>
                            <ThButton
                                width="18%"
                                ariaSort={vpAriaSort("assignment")}
                                onClick={() => toggleVpSort("assignment")}
                                title={t(M, "table.sortByAssignment")}
                            >
                                {t(M, "penalties.headers.assignment")} {vpGlyph("assignment")}
                            </ThButton>
                            <ThButton
                                width="12%"
                                ariaSort={vpAriaSort("rule")}
                                onClick={() => toggleVpSort("rule")}
                                title={t(M, "table.sortByRule")}
                            >
                                {t(M, "penalties.headers.rule")} {vpGlyph("rule")}
                            </ThButton>
                            <ThButton
                                width="8%"
                                ariaSort={vpAriaSort("override")}
                                onClick={() => toggleVpSort("override")}
                                title={t(M, "table.sortByOverride")}
                            >
                                {t(M, "penalties.headers.override")} {vpGlyph("override")}
                            </ThButton>
                            <ThButton
                                width="8%"
                                ariaSort={vpAriaSort("points")}
                                onClick={() => toggleVpSort("points")}
                                title={t(M, "table.sortByPoints")}
                            >
                                {t(M, "penalties.headers.points")} {vpGlyph("points")}
                            </ThButton>
                        </tr>
                        </thead>
                        <tbody>
                        {visiblePenalties.length === 0 ? (
                            <tr>
                                <td colSpan={8} style={{ textAlign: "center" }}>
                                    {t(M, "penalties.none")}
                                </td>
                            </tr>
                        ) : (
                            visiblePenalties.map((r, i) => {
                                const student = `${r.firstName || ""} ${r.lastName || ""}`.trim() || r.userId;
                                return (
                                    <tr key={`${r.courseId}-${r.userId}-${r.contentItemId}-${r.ruleCode}-${i}`}>
                                        <td>{r.termName || <span style={{ color: "#6b7280" }}>—</span>}</td>
                                        <td>{r.courseId || <span style={{ color: "#6b7280" }}>—</span>}</td>
                                        <td>{r.courseName || <span style={{ color: "#6b7280" }}>—</span>}</td>
                                        <td>{student}</td>
                                        <td>{r.assignmentTitle || <span style={{ color: "#6b7280" }}>—</span>}</td>
                                        <td>{r.ruleLabel || <span style={{ color: "#6b7280" }}>—</span>}</td>
                                        <td>{r.overridden ? t(M, "bool.yes") : t(M, "bool.no")}</td>
                                        <td>{Number.isFinite(r.points) ? r.points : 0}</td>
                                    </tr>
                                );
                            })
                        )}
                        </tbody>
                    </table>
                </section>
            )}
        </div>
    );
};

/** ---------- Tiny helpers / subcomponents ---------- */
const FieldError = ({ children }) => (
    <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>{children}</div>
);

const ThButton = ({ children, ariaSort, onClick, title, width }) => (
    <th
        role="button"
        tabIndex={0}
        aria-sort={ariaSort}
        onClick={onClick}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick()}
        title={title}
        style={{ width, cursor: "pointer", userSelect: "none", textAlign: "left", padding: "6px 4px" }}
    >
        {children}
    </th>
);

/** ---------------- Styles ---------------- */
const cardStyle = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 16 };
const h2Style = { fontSize: 18, fontWeight: "bold", margin: "0 0 8px" };
const listTableStyle = { width: "100%", borderCollapse: "collapse" };
const inputStyle = {
    width: "100%",
    padding: "6px 8px",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    background: "#fff",
};
const selectStyle = {
    width: "100%",
    padding: "6px 8px",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    background: "#fff",
};
const primaryBtn = { padding: "6px 12px", borderRadius: 8, background: "#111827", color: "#fff" };
const secondaryBtn = {
    padding: "6px 12px",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#111827",
};
const quietBtn = {
    padding: "6px 12px",
    borderRadius: 8,
    border: "1px dashed #9ca3af",
    background: "#fff",
    color: "#111827",
};
const tabBtn = { padding: "8px 12px", borderRadius: 999, border: "1px solid #e5e7eb" };
const tabBtnActive = { background: "#111827", color: "#fff" };
const tabBtnInactive = { background: "#fff", color: "#111827" };

// Only used by the delete button
const iconBtn = {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: "10px",
    lineHeight: "1",
    color: "#b91c1c",
    padding: 0,
    margin: 0,
};

const errorBoxStyle = { margin: "8px 0", padding: "8px", background: "#fef2f2", color: "#991b1b", borderRadius: 6 };
const toastStyle = { margin: "8px 0", padding: "8px", background: "#ecfdf5", color: "#065f46", borderRadius: 6 };
const sortIconStyle = { fontSize: 12, opacity: 0.6, marginLeft: 6 };

export default LatePenaltyRulesAdmin;

